<template>
  <div :class="'hero-section ' + 'property-1-' + property1">
    <div class="hero-background">
      <transition name="hero-transition">
        <video
          v-if="showVideo"
          class="globe-image"
          autoplay
          loop
          muted
          playsinline
          @loadeddata="handleVideoLoad"
          
        >
        <source :src="getCDNlink('assets/new-assets/Home_Banner_Video.webm')" type="video/webm"/>
      </video>
      </transition>
    </div>
    <div class="content-container">
      <div class="text-content">
        <div class="heading">
          <span class="heading-primary">
            Redefining <br />
            Business Intelligence <br />
            Through Technology
          </span>
        </div>
        <p class="subheading">
          Unlock smarter trade finance decisions with advanced insights into
          global counterparties using cutting-edge data solutions.<br />
          <span> Trusted by 35+ banks across Asia</span>
        </p>
      </div>
      <div class="search-container" @click="redirectToSearchPage">
        <transition name="expand-collapse">
          <div ref="searchBar" class="search-bar" :class="{ sticky: isSticky }">
            <div class="input-wrapper company-input">
              <input
                type="text"
                :placeholder="`${isMobileView ? 'Search Company' : $t('company-name-input-placeholder')}`"
                autocomplete="off"
                id="company_name"
                v-model="company_name"
                :readonly="isMobileView"
              />
            </div>
            <div class="input-wrapper country-input">
              <input
                type="text"
                placeholder="Country"
                :value="selectedCountry.name"
                @input="updateSelectedCountry"
                ref="searchInput"
                @click="showResults"
                :readonly="isMobileView"
              />
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                :class="{
                  'rotate-180': showResultsFlag,
                  'rotate-0': !showResultsFlag,
                }"
                @click="!isMobileView && toggleDropdown"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <div
                class="country-results"
                v-if="showResultsFlag"
                ref="countryResults"
              >
                <div
                  v-for="country in filteredCountries"
                  :key="country.id"
                  @click="selectCountry(country)"
                  class="country-option"
                >
                  <template v-if="isWindows()">
                    <img
                      :src="country.flagImage"
                      alt="Flag"
                      class="flag-image"
                    />
                    {{ country.name }}
                  </template>
                  <template v-else>
                    {{ country.flagEmoji }} {{ country.name }}
                  </template>
                </div>
              </div>
            </div>

            <button class="search-button" @click.prevent="search()" aria-label="Search">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="11" cy="11" r="7" stroke="white" stroke-width="2" />
                <path
                  d="M20 20L16 16"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
        </transition>
      </div>
    </div>
  </div>
  <Toastify ref="toastContainer" />
</template>

<script>
import { AllCountries } from "../../../api/APIs";
import Toastify from "@/components/Toastify.vue";
import { useI18n } from "vue-i18n";
import config from "@/config";

export default {
  name: "HeroSection",
  components: {
    Toastify,
  },
  props: {
    property1: {
      type: String,
      default: "default",
    },
  },
  data() {
    return {
      isPlaying: false,
      typeValue: "",
      typeStatus: false,
      displayTextArray: [],
      typingSpeed: 100,
      erasingSpeed: 100,
      newTextDelay: 2000,
      displayTextArrayIndex: 0,
      charIndex: 0,
      showTooltip: true,
      isSticky: false,
      company_name: "",
      selectedCountry: {},
      countries: [],
      filteredCountries: [],
      showResultsFlag: false,
      highQualityImageLoaded: false,
      isVideoLoaded: false,
      showVideo: false,
      isMobileView: window.innerWidth <= 480,
    };
  },
  setup() {
    const { locale, t } = useI18n();
    return { locale, t };
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler(newLang) {
        this.updateDisplayTextArray(newLang);
      },
    },
  },
  created() {
    setTimeout(this.typeText, this.newTextDelay + 200);
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    this.fetchCountryList();
    window.addEventListener("resize", this.handleResize);
    this.showVideo = true;
  },
  unmounted() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    handleResize() {
      this.isMobileView = window.innerWidth <= 480;
    },
    getCDNlink(fileKey) {
      let baseUrl = "";
      if (
        process.env.NODE_ENV === "development" ||
        process.env.NODE_ENV === "staging"
      ) {
        baseUrl = config.stagingCDN;
      } else if (process.env.NODE_ENV === "production") {
        baseUrl = config.prodCDN;
      }

      return `${baseUrl}${fileKey}`;
    },
    async updateDisplayTextArray(lang) {
      try {
        const jsonUrl = `${config.jsonStgCdn}new-home/${lang}.json`;
        const response = await fetch(jsonUrl);
        const data = await response.json();
        this.displayTextArray = data?.["typed-text"] || [];
      } catch (err) {
        this.fallbackEnglish();
      }
    },
    async fallbackEnglish() {
      try {
        const localEN = await import(`../../../../locales/new-home/en.json`);
        this.displayTextArray = localEN?.["typed-text"] || [];
      } catch (localError) {
        console.error("Failed to load local English JSON:", localError);
        this.displayTextArray = [];
      }
    },
    handleVideoLoad() {
      this.isVideoLoaded = true;
    },
    handleImageLoad() {
      const img = this.$refs.heroImage;
      if (!img || !img.dataset) {
        console.warn("heroImage ref not found or dataset missing");
        return;
      }

      if (this.highQualityImageLoaded) return;

      img.src = img.dataset.src;

      img.onload = () => {
        if (!this.highQualityImageLoaded) {
          this.highQualityImageLoaded = true;
        }
      };
    },
    redirectToSearchPage() {
      if (window.innerWidth <= 480) {
        this.$router.push({ name: "SearchForCompanies" });
      }
    },
    toggleDropdown() {
      this.showResultsFlag = !this.showResultsFlag;
      this.$refs.searchInput.focus();
      this.filteredCountries = this.countries;
    },
    isWindows() {
      return /Windows/.test(window.navigator.userAgent);
    },
    async search() {
      if (this.isMobileView) {
        this.redirectToSearchPage();
      } else {
        if (!this.company_name) {
          return this.$refs.toastContainer.addToast(
            "Company Name can not be empty.",
            { type: "error" }
          );
        } else if (this.company_name.trim().split(/\s+/).length < 2) {
          return this.$refs.toastContainer.addToast(
            "Please enter at least two words for the search.",
            { type: "warning" }
          );
        } else if (!this.selectedCountry.id) {
          return this.$refs.toastContainer.addToast(
            "Please select a Country first.",
            { type: "error" }
          );
        } else {
          const formattedCountry = this.selectedCountry.name.replace(
            /\s+/g,
            "-"
          );
          const formattedCompanyName = this.company_name
            .trim()
            .replace(/\s+/g, "-");
          const url = `/search/${formattedCountry}/${formattedCompanyName}`;
          window.location.href = url;
        }
      }
    },
    typeText() {
      if (
        this.charIndex <
        this.displayTextArray[this.displayTextArrayIndex].length
      ) {
        if (!this.typeStatus) this.typeStatus = true;
        this.typeValue += this.displayTextArray[
          this.displayTextArrayIndex
        ].charAt(this.charIndex);
        this.charIndex += 1;
        setTimeout(this.typeText, this.typingSpeed);
      } else {
        this.typeStatus = false;
        setTimeout(this.eraseText, this.newTextDelay);
      }
    },
    eraseText() {
      if (this.charIndex > 0) {
        if (!this.typeStatus) this.typeStatus = true;
        this.typeValue = this.displayTextArray[
          this.displayTextArrayIndex
        ].substring(0, this.charIndex - 1);
        this.charIndex -= 1;
        setTimeout(this.eraseText, this.erasingSpeed);
      } else {
        this.typeStatus = false;
        this.displayTextArrayIndex += 1;
        if (this.displayTextArrayIndex >= this.displayTextArray.length)
          this.displayTextArrayIndex = 0;
        setTimeout(this.typeText, this.typingSpeed + 1000);
      }
    },
    updateSelectedCountry(event) {
      this.selectedCountry = {
        ...this.selectedCountry,
        name: event.target.value,
      };
      this.filterCountries();
    },
    filterCountries() {
      const searchTerm = this.selectedCountry.name
        ? this.selectedCountry.name.toLowerCase()
        : "";
      this.filteredCountries = this.countries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm)
      );
      this.showResultsFlag = true;
    },
    selectCountry(country) {
      this.selectedCountry = country;
      this.showResultsFlag = false;
    },
    showResults() {
      if (window.innerWidth > 480) {
        this.showResultsFlag = true;
        this.filteredCountries = this.countries;
        document.addEventListener("click", this.closeResultsOnClickOutside);
      }
    },
    closeResultsOnClickOutside(event) {
      const searchInput = this.$refs.searchInput;
      const countryResults = this.$refs.countryResults;
      if (
        searchInput &&
        !searchInput.contains(event.target) &&
        countryResults &&
        !countryResults.contains(event.target)
      ) {
        this.showResultsFlag = false;
        document.removeEventListener("click", this.closeResultsOnClickOutside);
      }
    },
    handleScroll() {
      const textContent = document.querySelector(".text-content");
      if (textContent && textContent.getBoundingClientRect().top <= -26) {
        this.isSticky = true;
        this.showTooltip = false;
      } else if (
        textContent &&
        textContent.getBoundingClientRect().top >= -26
      ) {
        this.isSticky = false;
        this.showTooltip = true;
      }
    },
    async fetchCountryList() {
      try {
        const response = await AllCountries(false);
        this.countries = await response.allCountries;
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    },
  },
};
</script>

<style scoped>
.hero-section {
  position: relative;
  width: 100%;
  min-height: 101vh;
  background-color: #000000;
  /* overflow: hidden; */
  align-content: center;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.hero-transition-enter-active,
.hero-transition-leave-active {
  transition: opacity 0.5s ease;
}

.hero-transition-enter-from,
.hero-transition-leave-to {
  opacity: 0;
}

.globe-image {
  position: absolute;
  top: 0;
  right: -44%;
  /* width: 60%; */
  height: 100%;
  object-fit: cover;
  background-blend-mode: overlay;
  /* filter: blur(1.399999976158142px); */
}

.globe-image::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(0deg, #046BE1 0%, #046BE1 100%);
  background-blend-mode: overlay;
  filter: blur(1.399999976158142px);
  opacity: 0.8;
  z-index: 1;
}

.content-container {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  width: -webkit-fill-available;
  padding: 80px 160px;
}

.text-content {
  max-width: 650px;
  margin-bottom: 3rem;
}

.heading {
  width: 100%;
  position: relative;
  font-size: 68px;
  letter-spacing: -1.44px;
  line-height: 55.2px;
  font-weight: 500;
  font-family: Figtree;
  background: linear-gradient(135deg, #fff 0%, #ffece0 50%, #72b4ff 70%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: left;
  color:#fff;
  display: inline-block;
  margin: 0;
  will-change: transform;
  backface-visibility: hidden;
  font-display: swap;
}

.heading-primary {
  font-family: "Figtree";
  font-size: 68px;
  font-style: normal;
  font-weight: 500;
  line-height: 79.2px;
  letter-spacing: -1.44px;
}

.heading-secondary {
  font-size: 3.5rem;
  font-weight: 700;
  color: #046BE1;
  margin: 0;
  line-height: 1.2;
}

.subheading {
  font-family: "Figtree";
  font-size: 1.25rem;
  line-height: 1.4;
  color: #ffffff;
  max-width: 600px;
  margin-top: 1.5rem;
  text-align: left;
}

.search-container {
  position: relative;
  width: 100%;
  max-width: 690px;
  padding: 7px;
  background: #224096;
  border-radius: 50px;
  border: 1px solid rgba(224, 245, 255, 0.5);
}

.search-bar {
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 50px;
  padding: 0.25rem;
  border: 1px solid #046BE14d;
  /* box-shadow: 0 0 0 2px #046BE1, 0 0 20px rgba(1, 114, 244, 0.5); */
  transition: all 0.3s ease;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.input-wrapper:last-child {
  border-right: none;
}

.company-input {
  flex: 2;
}

.country-input {
  flex: 1;
  position: relative;
  border: 1px solid rgba(1, 114, 244, 0.3);
  border-radius: 8px;
}

.input-wrapper input {
  width: -webkit-fill-available;
  padding: 0 0 0 1.5rem;
  background: transparent;
  border: none;
  color: #1c1c1c;
  font-family: "Figtree";
  font-size: 1rem;
  outline: none;
  margin: 0;
  box-shadow: none;
  height: 44px;
}

.input-wrapper input::placeholder {
  color: #1c1c1c;
  opacity: 0.6;
}

.country-input svg {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.rotate-180 {
  transform: translateY(-50%) rotate(180deg) !important;
}

.rotate-0 {
  transform: translateY(-50%) rotate(0deg) !important;
}

.country-results {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.country-option {
  padding: 0.50rem 0.5rem;
  cursor: pointer;
  color: #1c1c1c;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
  text-align: left;
}

.country-option:hover {
  background-color: #e0f5ff;
}

.flag-image {
  width: 20px;
  height: 15px;
  object-fit: cover;
}

.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: #224096;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  margin-left: 0.5rem;
  margin-right: 0.25rem;
  transition: background-color 0.3s;
}

.subheading span {
  background: radial-gradient(
    25% 67% at 16% 39%,
    #fff 75%,
    #ffece0 36%,
    #72b4ff 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-top: 12px;
  position: absolute;
}

.search-button:hover {
  background: #224096;
}

input {
  outline: none !important;
}

input:focus {
  outline: none !important;
}
@keyframes blink {
  from,
  to {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

/* Responsive styles for tablet (767px) */
@media (max-width: 767px) {
  .globe-image {
    position: absolute;
    top: 66%;
    right: 0%;
  }
  .hero-section {
    min-height: 87vh;
    align-content: start;
  }
  /* .search-container .country-input,
  .search-container .country-results {
    display: none !important; 
  } */

  .search-container .company-input input {
    pointer-events: none; /* Make input non-editable */
    background-color: #fff; /* Optional: visually indicate it's disabled */
    cursor: pointer;
    padding: 0;
    font-size: 14px;
  }

  .search-container {
    border: none;
    padding: 0;
    margin-top:15px;
  }
  .content-container {
    padding: 14% 9% 5%;
  }

  .search-button {
    height: 47px;
    border-radius: 28.5px;
  }
  .heading {
    font-size: 48px;
    line-height: 41px;
  }

  .heading-primary {
    font-size: 36px;
    line-height: 47px;
  }

  .subheading {
    font-size: 16px;
    margin-top: 1rem;
  }

  .search-bar {
    flex-direction: row;
    padding: 0;
    border: 5px solid #224096;
  }

  .input-wrapper {
            padding: 0 15px;
        width: 30px;
        margin: 4px 0;
        padding-right: 6px;
  }

  .input-wrapper:last-child {
    border-bottom: none;
  }

  .company-input,
  .country-input {
    flex: 1;
  }

  .globe-image {
    width: 100%;
    opacity: 0.7;
    object-position: center;
  }

  .text-content {
    max-width: 100%;
    margin-bottom: 1rem;
  }

  .country-results {
    max-height: 180px;
  }
}

/* Responsive styles for mobile (480px) */
@media (max-width: 480px) {
  .search-container .company-input input,
  .country-input input {
    font-size: 13px;
  }
  .search-container {
    margin-top: 15px;
  }
  .heading {
    text-align: left;
  }
  .content-container {
    padding: 18% 4%;
  }
  .company-input,
  .country-input {
    padding: 0 15px;
    width: 20px;
    margin: 4px 0;
    padding-right: 6px;
  }
  .heading {
    font-size: 36px;
    line-height: 36px;
  }

  .heading-primary {
    font-size: 48px;
    letter-spacing: -1.44px;
    line-height: 56px;
  }

  .subheading {
    font-size: 16px;
    text-align: left;
    line-height: 1.3;
  }

  .input-wrapper input {
    padding: 0;
  }

  .search-button {
    height: 38px;
    width: 38px;
  }

  .sticky-search {
    bottom: 1rem;
    right: 1rem;
    width: 50px;
    height: 50px;
  }

  .country-option {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }

  .country-results {
    max-height: 160px;
  }

  .globe-image {
    opacity: 0.5;
  }
}

  .video-container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .play-button:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  .play-button i {
    color: white;
    font-size: 24px;
  }

  .globe-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

</style>
